import * as React from "react";
import { cn } from "@/lib/utils";
import { inputClass } from "./input";

interface TextareaProps extends React.ComponentProps<"textarea"> {
  /**
   * Grow with the content from `minRows` up to `maxRows`, then scroll: what MUI's
   * `multiline minRows maxRows` did. Measured from the element's own line height, so it
   * follows whatever text size the caller sets.
   */
  autoGrow?: { minRows?: number; maxRows: number };
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, autoGrow, rows, value, ...props }, forwardedRef) => {
    const innerRef = React.useRef<HTMLTextAreaElement | null>(null);
    const setRefs = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        innerRef.current = node;
        if (typeof forwardedRef === "function") forwardedRef(node);
        else if (forwardedRef) forwardedRef.current = node;
      },
      [forwardedRef]
    );

    const minRows = autoGrow?.minRows ?? 1;
    const maxRows = autoGrow?.maxRows;

    React.useLayoutEffect(() => {
      const node = innerRef.current;
      if (!node || maxRows === undefined) return;
      const style = getComputedStyle(node);
      const lineHeight = parseFloat(style.lineHeight) || 19.5;
      const chrome =
        parseFloat(style.paddingTop) +
        parseFloat(style.paddingBottom) +
        parseFloat(style.borderTopWidth) +
        parseFloat(style.borderBottomWidth);
      node.style.height = "auto";
      const max = lineHeight * maxRows + chrome;
      const height = Math.min(Math.max(node.scrollHeight, lineHeight * minRows + chrome), max);
      node.style.height = `${height}px`;
      node.style.overflowY = node.scrollHeight > max ? "auto" : "hidden";
    }, [minRows, maxRows, value]);

    return (
      <textarea
        ref={setRefs}
        data-slot="textarea"
        rows={autoGrow ? minRows : rows}
        value={value}
        className={cn(inputClass, "resize-none py-1.5 leading-[1.5]", className)}
        {...props}
      />
    );
  }
);
Textarea.displayName = "Textarea";

export { Textarea };
export type { TextareaProps };
